import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext();

// DEMO MODE: Auto-login bypass for testing
const DEMO_MODE = false; // Disabled - using real auth now
const DEMO_PLATFORM_OWNER = {
  id: 'platform_owner_1',
  username: 'admin',
  role: 'platform_owner', // New role!
  restaurant_id: null, // Platform owner has no specific restaurant
  created_at: new Date().toISOString()
};

const DEMO_RESTAURANT_ADMIN = {
  id: 'restaurant_admin_1',
  username: 'restaurant_admin',
  role: 'admin',
  restaurant_id: 'rest_demo_1', // Pizza Palace
  created_at: new Date().toISOString()
};

const DEMO_RESTAURANT_USER = {
  id: 'restaurant_user_1',
  username: 'user',
  role: 'user',
  restaurant_id: 'rest_demo_1', // Pizza Palace
  created_at: new Date().toISOString()
};

// Module-level throttle — persists across component remounts (page reloads,
// route-level Suspense, dev HMR etc.). Limits /auth/features to at most 1
// call per 3 seconds so rapid navigation can't spam the API.
let lastFeatureFetchTs = 0;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // DEMO MODE: Auto-login based on URL param or default
    if (DEMO_MODE) {
      const urlParams = new URLSearchParams(window.location.search);
      const role = urlParams.get('role');
      
      let demoUser;
      if (role === 'restaurant_admin') {
        demoUser = DEMO_RESTAURANT_ADMIN;
      } else if (role === 'user') {
        demoUser = DEMO_RESTAURANT_USER;
      } else {
        demoUser = DEMO_PLATFORM_OWNER; // Default: platform owner
      }
      
      console.log(`🎭 DEMO MODE: Auto-logged in as ${demoUser.role}`);
      setUser(demoUser);
      localStorage.setItem('demo_user', JSON.stringify(demoUser));
      setLoading(false);
      return;
    }
    
    // Normal auth check
    const token = localStorage.getItem('auth_token');
    const savedUser = localStorage.getItem('user');
    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch {
        // Corrupt saved user — wipe and force re-login.
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
      }
      // Verify the session is still valid on THIS build/server. This doubles
      // as a safety net for the Android "reinstall restored an old session
      // from Google Backup" class of bugs — a stale token will 401 here and
      // we wipe it immediately, landing the user on /login for a clean start.
      // Also refreshes `features` so newly-enabled modules appear without the
      // user needing a manual hard-refresh / relogin.
      authAPI.getMe()
        .then((me) => {
          authAPI.getFeatures().then((f) => {
            const nextUser = {
              username: me.username,
              role: me.role,
              restaurant_id: me.restaurant_id,
              features: f?.features || me.features || {},
              capabilities: me.capabilities || [],
              email: me.email || '',
            };
            setUser(nextUser);
            localStorage.setItem('user', JSON.stringify(nextUser));
          }).catch(() => {});
        })
        .catch((err) => {
          if (err?.response?.status === 401 || err?.response?.status === 403) {
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user');
            setUser(null);
          }
        });
    }
    setLoading(false);
  }, []);

  // Silently refresh features + identity from the server. Exposed through
  // context so components can fire it on navigation (see Sidebar) — gives
  // users near-instant propagation of module enables instead of waiting for
  // the focus/interval tick.
  //
  // Throttled at module level to at most 1 call every 3 seconds. The 60 s
  // safety-net interval + window focus listener still cover the "tab left
  // open for hours" case by passing force=true.
  const refreshFeatures = useCallback(async (force = false) => {
    if (!user || user.role === 'platform_owner') return;
    const now = Date.now();
    if (!force && now - lastFeatureFetchTs < 3000) return;
    lastFeatureFetchTs = now;
    try {
      const f = await authAPI.getFeatures();
      const nextFeatures = f?.features || {};
      const prev = JSON.stringify(user.features || {});
      const next = JSON.stringify(nextFeatures);
      if (prev !== next) {
        const updated = { ...user, features: nextFeatures };
        setUser(updated);
        localStorage.setItem('user', JSON.stringify(updated));
      }
    } catch {}
  }, [user]);

  // Poll the server for feature flag changes so platform-admin module
  // enables/disables propagate to the logged-in user WITHOUT them having to
  // log out. Runs on window focus + when the tab becomes visible again +
  // every 60 s as a safety net. Route-change refresh is wired from Sidebar.
  useEffect(() => {
    if (!user || user.role === 'platform_owner') return;
    // These paths bypass the 3-second debounce because they happen rarely
    // (user tabbing back, unlocking phone, or the 60-second safety net).
    const forceRefresh = () => refreshFeatures(true);
    const onVisibility = () => { if (document.visibilityState === 'visible') forceRefresh(); };
    const interval = setInterval(forceRefresh, 60000);
    window.addEventListener('focus', forceRefresh);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', forceRefresh);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [user, refreshFeatures]);

  const login = async (username, password) => {
    // DEMO MODE: Return different users based on username
    if (DEMO_MODE) {
      let demoUser;
      if (username === 'restaurant_admin' || username === 'rest_admin') {
        demoUser = DEMO_RESTAURANT_ADMIN;
      } else if (username === 'user' || username === 'staff') {
        demoUser = DEMO_RESTAURANT_USER;
      } else {
        demoUser = DEMO_PLATFORM_OWNER;
      }
      setUser(demoUser);
      return { user: demoUser };
    }
    
    // Generate or retrieve persistent device ID for binding
    let deviceId = null;
    const isTerminal = !!localStorage.getItem('heva_terminal');
    if (!isTerminal) {
      deviceId = localStorage.getItem('heva_device_id');
      if (!deviceId) {
        try {
          deviceId = 'dev_' + crypto.randomUUID();
        } catch {
          deviceId = 'dev_' + Date.now() + '_' + Math.random().toString(36).slice(2);
        }
        localStorage.setItem('heva_device_id', deviceId);
      }
    }

    // Try online login first
    try {
      const response = await authAPI.login(username, password, deviceId);
      const userData = {
        username: response.username,
        role: response.role,
        restaurant_id: response.restaurant_id,
        features: response.features || {},
        capabilities: response.capabilities || [],
        email: response.email || '',
      };
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      // Push notifications are opt-in from Settings — not auto-initialized on login
      // Cache credentials for offline login
      try {
        const hash = btoa(username + ':' + password);
        localStorage.setItem('offline_cred', hash);
        localStorage.setItem('offline_user', JSON.stringify(userData));
      } catch {}
      return { user: userData, ...response };
    } catch (error) {
      // Check for device binding block
      if (error.response?.status === 403 && error.response?.data?.detail?.startsWith('device_blocked:')) {
        const msg = error.response.data.detail.replace('device_blocked:', '');
        error.response.data.detail = msg;
        throw error;
      }
      // If offline, try cached credentials
      if (!navigator.onLine) {
        try {
          const cachedHash = localStorage.getItem('offline_cred');
          const attemptHash = btoa(username + ':' + password);
          if (cachedHash && cachedHash === attemptHash) {
            const cachedUser = JSON.parse(localStorage.getItem('offline_user'));
            if (cachedUser) {
              setUser(cachedUser);
              localStorage.setItem('user', JSON.stringify(cachedUser));
              return { user: cachedUser, offline: true };
            }
          }
        } catch {}
        throw new Error('Offline login failed. Credentials do not match cached session.');
      }
      throw error;
    }
  };

  const pinLogin = async (pin, restaurantId) => {
    try {
      const response = await authAPI.pinLogin(pin, restaurantId);
      const userData = {
        username: response.username,
        role: response.role,
        restaurant_id: response.restaurant_id,
        features: response.features || {},
        capabilities: response.capabilities || [],
      };
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      return { user: userData, ...response };
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    const lastRestId = user?.restaurant_id;
    if (lastRestId) {
      localStorage.setItem('last_restaurant_id', lastRestId);
    }
    // Teardown push listeners
    import('../services/push').then(m => m.teardownPushNotifications()).catch(() => {});
    setUser(null);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    localStorage.removeItem('demo_user');
  };

  // Role check helpers
  const isPlatformOwner = user?.role === 'platform_owner';
  const isRestaurantAdmin = user?.role === 'admin';
  const isRestaurantUser = user?.role === 'user';
  const canAccessRestaurants = isPlatformOwner; // Only platform owner

  // Feature check helper - reads from user.features (embedded in JWT).
  // Memoized so consumers using it as a useMemo/useEffect dep don't thrash.
  const hasFeature = useCallback((featureName) => {
    if (isPlatformOwner) return true; // Platform owner sees everything
    return user?.features?.[featureName] === true;
  }, [isPlatformOwner, user]);

  // Capability check helper - reads from user.capabilities array
  const hasCapability = useCallback((cap) => {
    if (isPlatformOwner || isRestaurantAdmin) return true;
    return (user?.capabilities || []).includes(cap);
  }, [isPlatformOwner, isRestaurantAdmin, user]);

  // Device mode check
  const isTerminalMode = !!localStorage.getItem('heva_terminal');

  const features = user?.features || {};
  const capabilities = user?.capabilities || [];

  return (
    <AuthContext.Provider value={{ 
      user, 
      login,
      pinLogin,
      logout, 
      loading, 
      isPlatformOwner,
      isRestaurantAdmin,
      isRestaurantUser,
      canAccessRestaurants,
      isAdmin: isPlatformOwner || isRestaurantAdmin,
      hasFeature,
      hasCapability,
      refreshFeatures,
      isTerminalMode,
      features,
      capabilities,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
